// Standardized client-deliverable filename convention: {projectNumber}_{code}_{type}_{yyyymmdd}.{ext}
// projectNumber falls back to "0000" for projects that don't have one set yet in Project Settings.
const KIND_SUFFIX = {
  slip_plot: { suffix: "slip_plot", ext: "pdf" },
  slip_inventory_geojson: { suffix: "slip_inventory", ext: "geojson" },
  slip_inventory_csv: { suffix: "slip_inventory", ext: "csv" },
  slam_tif: { suffix: "SLAM", ext: "tif" },
  // LAS carries the export CRS in the filename itself (no sidecar/.prj to check) --
  // deliverableFilename throws if epsgCode is missing for this kind.
  slam_las: { suffix: "SLAM", ext: "las", needsEpsg: true },
};

export function deliverableDateStamp(date = new Date()) {
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}`;
}

export function sanitizeDeliverableCode(code) {
  return String(code || "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
}

// "Four digit numeric marina identifier" -- zero-pad a shorter numeric value (e.g. Project
// Settings has "42") up to the required width; leave non-numeric input alone rather than
// silently mangling it into something that looks valid but isn't.
export function sanitizeDeliverableProjectNumber(projectNumber) {
  const trimmed = String(projectNumber || "").trim();
  if (!trimmed) return "0000";
  return /^\d+$/.test(trimmed) ? trimmed.padStart(4, "0") : trimmed;
}

// Region suffix for a marina with multiple regions (see slip-delivery's Generate
// Deliverables): the main/first region's files keep the plain code with no suffix, every
// other region's PDF/LAS/TIFF get "-REGION-SLUG" appended to the code. Uppercased with any
// run of non-alphanumeric characters (spaces included) collapsed to a single dash, so "North
// Yard" becomes "NORTH-YARD".
export function sanitizeDeliverableRegionSlug(region) {
  return String(region || "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export function deliverablePrefix({ projectNumber, code, region }) {
  const regionSlug = sanitizeDeliverableRegionSlug(region);
  const codePart = regionSlug ? `${sanitizeDeliverableCode(code)}-${regionSlug}` : sanitizeDeliverableCode(code);
  return `${sanitizeDeliverableProjectNumber(projectNumber)}_${codePart}_`;
}

export function deliverableFilename({ projectNumber, code, kind, date, epsgCode, region }) {
  const entry = KIND_SUFFIX[kind];
  if (!entry) throw new Error(`Unknown deliverable kind: ${kind}`);
  const { suffix, ext, needsEpsg } = entry;
  let epsgPart = "";
  if (needsEpsg) {
    const digits = String(epsgCode || "").replace(/[^0-9]/g, "");
    if (!digits) throw new Error(`deliverableFilename: kind "${kind}" requires epsgCode`);
    epsgPart = `_EPSG${digits}`;
  }
  return `${deliverablePrefix({ projectNumber, code, region })}${suffix}_${deliverableDateStamp(date)}${epsgPart}.${ext}`;
}

export function deliverableKinds() {
  return Object.keys(KIND_SUFFIX);
}
